<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <h4 class="mb-50">
        {{ $t('ledger_inv_statement') }}
      </h4>
      <div v-if="statement && statement.customer">
        {{ statement.customer.name }}
        <span class="text-muted">{{ statement.customer.customer_number }}</span>
      </div>
      <b-row class="mt-1">
        <b-col
          cols="6"
          md="3"
        >
          <b-form-input
            v-model="from"
            type="date"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-input
            v-model="to"
            type="date"
          />
        </b-col>
        <b-col
          cols="12"
          md="2"
        >
          <b-button
            variant="primary"
            class="rounded-pill"
            @click="load"
          >
            {{ $t('ledger_assets_load') }}
          </b-button>
        </b-col>
      </b-row>
      <div
        v-if="statement"
        class="ledger-inv-kpis mt-1"
      >
        <div><span class="text-muted">{{ receivableCode }}</span><strong class="ledger-num d-block">{{ money(statement.receivable_balance) }}</strong></div>
        <div><span class="text-muted">1135</span><strong class="ledger-num d-block">{{ money(statement.retention_balance) }}</strong></div>
        <div><span class="text-muted">2170</span><strong class="ledger-num d-block">{{ money(statement.advance_balance) }}</strong></div>
        <div><span class="text-muted">{{ $t('ledger_inv_opening') }}</span><strong class="ledger-num d-block">{{ money(statement.opening_balance) }}</strong></div>
        <div><span class="text-muted">{{ $t('ledger_inv_closing') }}</span><strong class="ledger-num d-block">{{ money(statement.closing_balance) }}</strong></div>
      </div>
    </b-card>

    <b-card
      v-if="statement"
      class="rounded-lg"
    >
      <h5>{{ $t('ledger_inv_movements') }}</h5>
      <div class="d-none d-md-block">
        <b-table
          :items="statement.movements || []"
          :fields="moveFields"
          small
          responsive
          class="ledger-dense-table"
        >
          <template #cell(debit)="data">
            <span class="ledger-num">{{ money(data.item.debit) }}</span>
          </template>
          <template #cell(credit)="data">
            <span class="ledger-num">{{ money(data.item.credit) }}</span>
          </template>
          <template #cell(balance)="data">
            <span class="ledger-num">{{ money(data.item.balance) }}</span>
          </template>
        </b-table>
      </div>
      <div class="d-md-none">
        <article
          v-for="(row, index) in statement.movements || []"
          :key="index"
          class="ledger-inv-card"
        >
          <div>{{ row.date }} · {{ row.account_code }}</div>
          <div>{{ row.description }}</div>
          <div class="ledger-num">
            {{ money(row.balance) }}
          </div>
        </article>
      </div>
      <h5 class="mt-2">
        {{ $t('ledger_inv_title') }}
      </h5>
      <article
        v-for="row in statement.invoices || []"
        :key="row.id"
        class="ledger-inv-card"
      >
        <b-link :to="{ name: 'ledger-invoices-view', params: { id: row.id } }">
          {{ row.invoice_number }}
        </b-link>
        <span
          v-if="row.kind"
          class="text-muted small ml-50"
        >
          {{ enumLabel(row.kind, $i18n.locale) }}
        </span>
        <b-badge
          v-if="row.is_overdue"
          variant="danger"
          class="ml-50"
        >
          {{ $t('ledger_inv_overdue') }}
        </b-badge>
        <div class="ledger-num">
          {{ $t('ledger_ct_retention') }} {{ money(row.retention_amount) }}
        </div>
        <div class="ledger-num">
          {{ $t('ledger_inv_balance') }} {{ money(row.balance_due) }}
        </div>
      </article>
    </b-card>
    <div
      v-if="statement"
      class="ledger-inv-sticky d-md-none"
    >
      <span>{{ receivableCode }}</span>
      <strong class="ledger-num">{{ money(statement.receivable_balance) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BBadge, BLink,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getCustomerStatement } from '@/api/accounting/invoices'
import {
  oneFrom, formatLedgerMoney, todayIso, apiErrorMessage, enumLabel, glAccountCode,
} from '@/utils/ledger'

function monthStart() {
  const date = new Date()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  return `${date.getFullYear()}-${m}-01`
}

export default {
  name: 'CustomerStatement',
  components: {
    BCard, BRow, BCol, BFormInput, BButton, BTable, BBadge, BLink,
  },
  data() {
    return {
      from: monthStart(),
      to: todayIso(),
      statement: null,
    }
  },
  computed: {
    receivableCode() {
      return glAccountCode(this.statement && this.statement.customer) || '—'
    },
    moveFields() {
      return [
        { key: 'date', label: this.$t('ledger_date') },
        { key: 'entry_number', label: this.$t('ledger_entry_number') },
        { key: 'account_code', label: this.$t('ledger_code') },
        { key: 'description', label: this.$t('ledger_description') },
        { key: 'debit', label: this.$t('ledger_debit') },
        { key: 'credit', label: this.$t('ledger_credit') },
        { key: 'balance', label: this.$t('ledger_inv_running') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    async load() {
      try {
        const res = await getCustomerStatement(this.$route.params.id, { from: this.from, to: this.to })
        this.statement = oneFrom(res)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-inv-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.ledger-inv-card { border-bottom: 1px solid #eee; padding: 0.75rem 0.15rem; }
.ledger-inv-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
